import { useCallback } from "react";
import { create } from "zustand";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";

export type ToastSeverity = "success" | "info" | "warning" | "error";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  message: string;
  severity: ToastSeverity;
  duration: number;
  action?: ToastAction;
}

interface ToastStore {
  toasts: Toast[];
  push: (t: Omit<Toast, "id">) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;

const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (t) => {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

const defaultDuration = (severity: ToastSeverity, hasAction: boolean) =>
  hasAction ? 6000 : severity === "error" ? 8000 : 4000;

/** Fire a toast from outside React (interceptors, query cache, live events). */
export function notify(
  message: string,
  severity: ToastSeverity = "info",
  action?: ToastAction,
): number {
  return useToastStore.getState().push({
    message,
    severity,
    action,
    duration: defaultDuration(severity, Boolean(action)),
  });
}

export function dismissToast(id: number) {
  useToastStore.getState().dismiss(id);
}

export interface ToastApi {
  toast: (
    message: string,
    severity?: ToastSeverity,
    opts?: { duration?: number; action?: ToastAction },
  ) => number;
  success: (message: string, action?: ToastAction) => number;
  info: (message: string, action?: ToastAction) => number;
  warning: (message: string, action?: ToastAction) => number;
  error: (message: string, action?: ToastAction) => number;
}

export function useToast(): ToastApi {
  const push = useToastStore((s) => s.push);
  const toast = useCallback<ToastApi["toast"]>(
    (message, severity = "info", opts) =>
      push({
        message,
        severity,
        action: opts?.action,
        duration: opts?.duration ?? defaultDuration(severity, Boolean(opts?.action)),
      }),
    [push],
  );
  return {
    toast,
    success: useCallback((m, action) => toast(m, "success", { action }), [toast]),
    info: useCallback((m, action) => toast(m, "info", { action }), [toast]),
    warning: useCallback((m, action) => toast(m, "warning", { action }), [toast]),
    error: useCallback((m, action) => toast(m, "error", { action }), [toast]),
  };
}

/** Renders the toast queue. Mount once, near the app root. */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <Stack
      spacing={1}
      sx={{ position: "fixed", bottom: 16, right: 16, zIndex: (t) => t.zIndex.snackbar }}
    >
      {toasts.map((t) => (
        <Snackbar
          key={t.id}
          open
          autoHideDuration={t.duration}
          onClose={(_, reason) => {
            if (reason !== "clickaway") dismiss(t.id);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ position: "static", transform: "none" }}
        >
          <Alert
            severity={t.severity}
            variant="filled"
            onClose={() => dismiss(t.id)}
            action={
              t.action ? (
                <Button
                  size="small"
                  color="inherit"
                  sx={{ fontWeight: 700 }}
                  onClick={() => {
                    t.action?.onClick();
                    dismiss(t.id);
                  }}
                >
                  {t.action.label}
                </Button>
              ) : undefined
            }
            sx={{ minWidth: 280, boxShadow: 3, alignItems: "center" }}
          >
            {t.message}
          </Alert>
        </Snackbar>
      ))}
    </Stack>
  );
}
